// initialise.ts

import { setupErrorReporting } from './utils/errors';
import { initialiseConsentSystem } from './consent/initialiseConsent';
import { ConsentUpdate, hasUserSetConsent } from './consent/consentManager';
import { registerCallback } from './consent/callbacks';
import { dispatchConsentUpdate } from './events/consentUpdateEvent';
import {
	attachServerStylesheets,
	getBaseFontSize,
	injectScript,
	setBaseFontSize,
} from './utils/dom';
import TagManagerConsent from './tracking_scripts/tag_manager/consent';
import { initialiseGTM } from './tracking_scripts/tag_manager/init';
import { EtmcData, initializeEtmcTracking } from './tracking_scripts/marketingCloud';
import { initializePaq } from './tracking_scripts/tag4arm';
import { getCookie } from './utils/cookies';
import { applyFocusTrap } from './utils/focusTrap';
import { NotificationService } from './utils/NotificationService';

// types.d.ts

interface ServerData {
	contextPath: string;
	storeName: string;
	stylesheetLinks: any;
	universal_variable?: any;
	jsErrorsEnabled: boolean;
	cookieBannerEnabled: boolean;
	googleAnalyticsEnabled: boolean;
	cookiesAccepted?: boolean;
	cookiesNoticeCopy?: string;
	cookiesBannerAcceptBtnCopy?: string;
	cookiesBannerRejectBtnCopy?: string;
	cookiesBannerManageBtnCopy?: string;
	googleAnalyticsCode?: string;
	GTMLoggingEnabled?: boolean;
	webtrendsEnabled?: boolean;
	tag4armEnabled?: boolean;
	marketingCloudEnabled?: boolean;
	turnstileSiteKey?: string;
	turnstileErrorMessage?: string;
	turnstileInteractionMessage?: string;
	turnstileForceVisible?: boolean;
	exactTargetOrgId?: string;
	userKnown?: string;
	customerEmail?: string;
	customerNumber?: string;
	pageName?: string;
	productCode?: string;
	productCategory?: string;
	pcaKey?: string;
	pcaEnabled?: string;
	w3wEnabled?: boolean;
	convivaEnabled?: boolean;
	ageProtected?: string;
	ageGateModalHeading?: string;
	ageGateModalCopy?: string;
	ageGateModalButtonCopy?: string;
	GTM_list?: any;
	rafReward?: string;
	customerFirstName?: string;
	referAFriendCode?: string;

	// Add other properties as needed
}

// Extend the Window interface with the serverData property
declare global {
	interface Window {
		serverData: ServerData;
		universal_variable: any;
		dataLayer: any;
		apptracker?: (event: string, data: { title: string }) => void;
		voucherConflict: number;
		vcCode: string;
		vcDiscount: string;
		vcDiscountType: string;
		vcMessage: string;
		windowWidth: any;
	}
}

NotificationService.listenForEvents();

document.addEventListener('DOMContentLoaded', () => {
	/**
	 * Attaches all stylesheet links defined in `window.serverData.stylesheetLinks` to the document head.
	 *
	 * The function uses the `window.serverData` object to retrieve the list of stylesheet links and attaches them to the specified target.
	 * In this case, it attaches the stylesheets to `document.head` to make them globally available.
	 *
	 * @example
	 * // Assuming window.serverData.stylesheetLinks contains:
	 * // [
	 * //   "https://fonts.googleapis.com/css2?family=Roboto:wght@100;300;400;700&display=swap",
	 * //   "/dist/css/main-vw.css"
	 * // ]
	 * attachServerStylesheets(document.head);
	 * // This will add the provided links to the document head.
	 */
	attachServerStylesheets(document.head).then(() => {
		/**
		 * Sets the css variable for default document font size.
		 */
		setBaseFontSize();
		console.assert(getBaseFontSize(), 'Default document font size: ' + getBaseFontSize());
		document.documentElement.classList.add('initialised');
	});

	/**
	 * Set the value of the window interface 'window width' variable
	 */
	window.windowWidth = window.innerWidth;

	/**
	 * Sets up JavaScript error reporting if enabled in server data.
	 */
	if (window.serverData.jsErrorsEnabled) {
		setupErrorReporting(window.serverData.contextPath);
	}

	/**
	 * Initializes the Universal Variable if it exists in server data.
	 */
	if (window.serverData.universal_variable) {
		window.universal_variable = window.serverData.universal_variable;
	}

	/**
	 * Sets the scroll restoration to manual to control page scroll behavior.
	 */
	history.scrollRestoration = 'manual';

	/**
	 * Initializes the consent system by setting up custom event listeners.
	 */
	initialiseConsentSystem();

	/**
	 * Handles the display of the cookie consent modal if the cookie banner is enabled
	 * and no consent has been set by the user.
	 */
	if (window.serverData.cookieBannerEnabled) {
		if (!hasUserSetConsent()) {
			// If no consent cookie is found, create and display the cookie consent modal
			const cookieConsentModal = document.createElement('wc-consent-modal');

			document.body.appendChild(cookieConsentModal);
		}
	} else {
		// If the cookie banner is not enabled, default consent to true for all types
		const consentUpdates: ConsentUpdate[] = [
			{ consentType: 'essential', consentGiven: true },
			{ consentType: 'functional', consentGiven: true },
			{ consentType: 'marketing', consentGiven: true },
		];

		// Dispatches a custom event with default consent updates if the cookie banner is not enabled.
		dispatchConsentUpdate(consentUpdates);
	}

	/**
	 * Registers a callback for initializing Google Tag Manager (GTM) and analytics scripts
	 * when essential consent is granted.
	 */
	registerCallback(
		'essential',
		function () {
			// Initialize consent management for GTM
			const consentManager = new TagManagerConsent();
			consentManager.updateConsent();

			window.dataLayer.push({
				'gtm.start': new Date().getTime(),
				'event': 'gtm.js',
			});

			const gtmScriptSrc = `https://www.googletagmanager.com/gtm.js?id=${window.serverData.googleAnalyticsCode}`;
			injectScript(gtmScriptSrc, true, false, () => {
				console.log('Google Tag Manager Loaded!');
			});

			initialiseGTM();
		},
		'grant',
	);

	/**
	 * Registers a callback for loading Webtrends when functional consent is granted.
	 */
	registerCallback(
		'functional',
		function () {
			if (window.serverData.webtrendsEnabled) {
				injectScript(
					'//c.webtrends-optimize.com/acs/accounts/13458b9d-9a22-462e-b779-f3e2c749a8c8/js/wt.js',
					true,
					false,
					() => {
						console.log('Webtrends Loaded!');
					},
				);
			}
		},
		'grant',
	);

	/**
	 * Registers a callback for initializing Marketing Cloud tracking scripts when marketing consent is granted.
	 */
	registerCallback(
		'marketing',
		function () {
			if (window.serverData.marketingCloudEnabled) {
				injectScript('//6261229.collect.igodigital.com/collect.js', true, false, () => {
					console.log('Marketing Cloud Loaded!');
				});

				const etmcData: EtmcData = {
					exactTargetOrgId: window.serverData.exactTargetOrgId as string | undefined,
					userKnown: window.serverData.userKnown as boolean | undefined,
					customerEmail: window.serverData.customerEmail as string | undefined,
					pageName: window.serverData.pageName as string | undefined,
					productCode: window.serverData.productCode as string | undefined,
					productCategory: window.serverData.productCategory as string | undefined,
				};

				initializeEtmcTracking(etmcData);
			}
		},
		'grant',
	);

	/**
	 * Registers a callback for loading Tag4Arm tracking scripts when marketing consent is granted.
	 */
	registerCallback(
		'marketing',
		function () {
			if (window.serverData.tag4armEnabled) {
				const trackerUrl = initializePaq();
				injectScript(trackerUrl, true, true, () => {
					console.log('tag4arm Loaded!');
				});
			}
		},
		'grant',
	);

	/**
	 * Display the 'Are you over 18?' modal for pages with the associated attribute set to true, as detected via ageProtected, and if the user hasn't seen the modal yet, as detected with the cookie.
	 */
	if (window.serverData.ageProtected == 'true' && !getCookie('over18Confirmed')) {
		const ageGateModal = document.createElement('wc-age-gate-modal');
		document.body.appendChild(ageGateModal);
		document.body.classList.add('modal-active');

		const ageGateModalElem = document.querySelector('wc-age-gate-modal');
		ageGateModalElem?.addEventListener('age-gate-approved', () => {
			const consentModalElem = document.querySelector('wc-consent-modal');
			if (!consentModalElem) return;
			const consentModal = consentModalElem.shadowRoot?.querySelector(
				'.wc-consent-modal',
			) as HTMLElement;
			applyFocusTrap(consentModal, true);
		});
	}
});
